// angular imports
import {
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { DxDataGridComponent } from 'devextreme-angular';

// 3rd party imports
import { forkJoin } from 'rxjs';

// cc-common imports
import { MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';

// model imports
import { WorkOrderResultDto } from '../../_models/work-order-result-dto';
import { WorkOrderStatusChangeParametersDto } from '../../_models/work-order-status-change-parameters-dto';
import { DocumentTreeResultDto } from '../../../shared-document-module/_models/document-tree-result-dto';
import { FileInfoDisplayResultDto } from '../../../_models/file-info-display-result-dto';
import { BasePropertyParametersDto } from '../../_models/base-property-parameter-dto';
import { BaseWorkOrderParameterDto } from '../../_models/base-work-order-parameter-dto';
import { WorkOrderAttachmentParametersDto } from '../../_models/work-order-attachment-parameters-dto';
import { WorkOrderOwnerDocumentsParameterDto } from '../../_models/work-order-owner-documents-parameter-dto';
import { WorkOrderAttachmentRawDto } from '../../_models/work-order-attachment-raw-dto';
import { WorkOrderManageAttachmentsParametersDto } from '../../_models/work-order-manage-attachments-parameters-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';
import { UserNameDisplayResultDto } from '../../../_models/user-name-display-result-dto';
import { WorkOrderCreateJobResultDto } from '../../_models/work-order-create-job-result-dto';
import { WorkOrderWorkAuthorizationFileParameterDto } from '../../_models/work-order-work-authorization-file-parameter-dto';
import { WorkOrderDocumentResultDto } from '../../_models/work-order-document-result-dto';

// constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StatusConstants } from '../../_constants/status-constants';
import { WorkOrderActions } from '../../_constants/workorder-action-constants';

interface MultiUserNameDisplayInfo {
    users: Array<UserNameDisplayResultDto>;

    communityId: number;
    title: string;
}

@Component({
    selector: 'app-work-order-grid',
    templateUrl: './work-order-grid.component.html',
    styleUrls: ['./work-order-grid.component.scss']
})
export class WorkOrderGridComponent extends WorkOrderBaseComponent implements OnInit, OnChanges {
    @ViewChild('workOrderMessageBox', { static: false }) workOrderMessageBox: MessageBoxComponent;
    @ViewChild('workOrderGrid', { static: false }) workOrderGrid: DxDataGridComponent;

    @Input() workOrdersGridSource: Array<WorkOrderResultDto>;
    @Input() workOrderStatus: string;
    @Input() isPopup = false;
    @Input() workOrderIds: Array<number> = [];
    @Input() showOnlyLinked = false;

    @Output() onForceReload = new EventEmitter<void>();
    @Output() onWorkOrderSelection = new EventEmitter<number[]>();
    @Output() onWorkOrderDeselection = new EventEmitter<number[]>();

    selectedWorkOrder: WorkOrderResultDto;
    showWorkOrderEditor = false;

    showUsersListPopup = false;
    showHistory = false;
    showAttachments = false;
    showManageWorkAuthorization = false;
    showManageProposals = false;

    workOrderAttachments: Array<WorkOrderDocumentResultDto> = [];
    communityDocuments: Array<DocumentTreeResultDto> = [];
    ownerDocuments: Array<DocumentTreeResultDto> = [];

    storageKey: string;

    uniqueInvoiceIds: Array<any> = [];
    uniqueTaskIds: Array<any> = [];

    actionMenuItems: Array<ActionMenuInfo> = [];

    showManageInvoices = false;
    showManageTask = false;

    showTaskViewer = false;
    selectedTaskId: number;

    showWorkflowViewer = false;
    selectedWorkflowId: number;

    selectedUserConfig: MultiUserNameDisplayInfo;
    uniqueDashboardResponsibleUsers: Array<any> = [];
    uniquePrimaryAcamUsers: Array<any> = [];
    uniquePrimaryCamUsers: Array<any> = [];

    constructor(protected readonly injector: Injector) {
        super(injector);
        this.isActionItemVisible = this.isActionItemVisible.bind(this);
    }

    ngOnInit() {
        this.prepareUniqueUsers();
        this.prepareActionItems();
        this.storageKey = `${this.workOrderStatus.toUpperCase()}_${this.isPopup.toString()}_WORK_ORDER_GRID`;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.workOrdersGridSource && changes.workOrdersGridSource.currentValue) {
            this.workOrdersGridSource = changes.workOrdersGridSource.currentValue;
            this.prepareUniqueIds();
        }
    }

    onGridContentReady(): void {
        if (Utils.equalsIgnoreCase(this.workOrderStatus, 'closed')) {
            this.workOrderGrid.instance.columnOption('location', 'groupIndex', 0);
            this.workOrderGrid.instance.columnOption('community', 'groupIndex', 1);
        }

        if (Utils.notNullOrEmpty(this.workOrderIds) && Utils.isFalse(this.showOnlyLinked)) {
            this.workOrderGrid.instance.selectRows(this.workOrderIds, true);
        }
    }

    onActionItemClick(event: ActionMenuEvent<WorkOrderResultDto>): void {
        this.selectedWorkOrder = event.record;
        const actionItem = event.item.action;
        if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.MANAGE_WORK_AUTHORIZATION)) {
            this.onManageWorkAuthorizationShow();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.MANAGE_PROPOSALS)) {
            this.onManageProposalShow();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.GENERATE_WORK_AUTHORIZATION)) {
            this.generateWorkAuthorization(this.selectedWorkOrder.communityId, this.selectedWorkOrder.workOrderId);
        } else if (Utils.equalsIgnoreCase(event.item.parent, WorkOrderActions.CHANGE_STATUS)) {
            this.changeStatus(actionItem);
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.GENERATE_AND_EMAIL_WORK_AUTHORIZATION)) {
            this.generateWorkAuthorization(
                this.selectedWorkOrder.communityId,
                this.selectedWorkOrder.workOrderId,
                true
            );
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.MANAGE_INVOICE_REFERENCES)) {
            this.showManageInvoices = true;
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.MANAGE_TASK_REFERENCES)) {
            this.showManageTask = true;
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.EMAIL_LATEST_WORK_AUTHORIZATION)) {
            this.emailWorkAuthorization(this.selectedWorkOrder.communityId, this.selectedWorkOrder.workOrderId);
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.VIEW_OR_EDIT_WORKORDER)) {
            this.onWorkOrderEditorShow();
        } else if (Utils.equalsIgnoreCase(actionItem, WorkOrderActions.CREATE_MAINTENANCE_JOB)) {
            this.createMaintenanceJob();
        }
    }

    showUsersList(event: MultiUserNameDisplayInfo): void {
        this.selectedUserConfig = event;
        this.showUsersListPopup = true;
    }

    onUserListClose(): void {
        this.showUsersListPopup = false;
        this.selectedUserConfig = null;
    }

    onVendorNameClick(data: WorkOrderResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            const contextType = 'Community';
            const contextId = data.communityId;
            const vendorCode = data.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;

            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    onWorkOrderCellClick(record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        if (Utils.isFalse(this.isPopup)) {
            this.openWorkOrderFile();
        } else {
            const queryString = `?communityId=${record.communityId}&workOrderId=${record.workOrderId}`;
            const url = `my-work/workorder${queryString}`;
            this.helperService.openWindow(url, '_blank', null, null);
        }
    }

    onMaintenanceJobClick(record: WorkOrderResultDto): void {
        // NOTE: This feature is not available in V2 as per discussion with Sandeep.
    }

    onTaskIdClick(id: number, record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        this.selectedTaskId = id;

        this.showTaskViewer = true;
    }

    onInvoiceClick(invoice: number, record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        this.selectedWorkflowId = invoice;
        this.showWorkflowViewer = true;
    }

    onWorkflowViewerClose(): void {
        this.showWorkflowViewer = false;
    }

    onGridNotesClick(record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        this.showNotes = true;
    }

    onGridNotesClose(forceReload: boolean): void {
        this.onNotesClose();

        if (Utils.isTrue(forceReload)) {
            this.forceReloadWorkOrder();
        }
    }

    onHistoryClick(record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        this.showHistory = true;
    }

    onHistoryClose(): void {
        this.showHistory = false;
    }

    onAttachmentClick(record: WorkOrderResultDto): void {
        this.selectedWorkOrder = record;
        this.populateAttachments();
        this.showAttachments = true;
    }

    onAttachmentUpdate(allFiles: any[]): void {
        const newAttachments: Array<WorkOrderAttachmentRawDto> = [];
        allFiles.forEach((file) => {
            if (Utils.notNullAndDefined(file.action)) {
                const obj = new WorkOrderAttachmentRawDto();
                obj.fileName = Utils.notNullOrEmpty(file.fileId) ? file.fileName : file.name;
                obj.fileId = Utils.notNullOrEmpty(file.fileId) ? file.fileId : null;
                obj.fileBase64String = file.fileBase64String;
                obj.crudAction = file.action;
                newAttachments.push(obj);
            }
        });

        this.uploadAttachments(newAttachments);
    }

    onAttachmentDelete(attachment: FileInfoDisplayResultDto): void {
        const deleteDialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            'Are you sure to delete the selected file?',
            'Yes',
            'No'
        );
        deleteDialog.show().then((confirmation: boolean) => {
            if (Utils.isTrue(confirmation)) {
                this.deleteAttachment(attachment);
            }
        });
    }

    onAttachmentUploaderClose(): void {
        this.showAttachments = false;
        this.forceReloadWorkOrder();
    }

    isActionItemVisible(item: ActionMenuInfo, record: WorkOrderResultDto): boolean {
        return (
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.VIEW_OR_EDIT_WORKORDER) &&
                Utils.isTrue(record.allowAction)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.VIEW_MAINTENANCE_JOB) &&
                Utils.isTrue(record.allowViewMaintenanceJob)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.GENERATE_WORK_AUTHORIZATION) &&
                Utils.isTrue(record.allowGenerateWorkAuthorization)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.GENERATE_AND_EMAIL_WORK_AUTHORIZATION) &&
                Utils.isTrue(record.allowGenerateAndEmailWorkAuthorization)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.EMAIL_LATEST_WORK_AUTHORIZATION) &&
                Utils.isTrue(record.allowEmailLatestWorkAuthorization)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.CHANGE_STATUS) &&
                Utils.isTrue(record.allowChangeStatus)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.MANAGE_WORK_AUTHORIZATION) &&
                Utils.isTrue(record.allowAction)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.MANAGE_PROPOSALS) &&
                Utils.isTrue(record.allowAction)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.MANAGE_TASK_REFERENCES) &&
                Utils.isTrue(record.allowAction)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.MANAGE_INVOICE_REFERENCES) &&
                Utils.isTrue(record.allowManageInvoiceReferences)) ||
            (Utils.equalsIgnoreCase(item.action, WorkOrderActions.CREATE_MAINTENANCE_JOB) &&
                Utils.isTrue(record.allowCreateMaintenanceJob))
        );
    }

    onManageInvoicesClose(forceReload: boolean): void {
        this.showManageInvoices = false;
        if (Utils.isTrue(forceReload)) {
            this.onForceReload.emit();
        }
    }

    onManageTaskClose(forceReload: boolean): void {
        this.showManageTask = false;
        if (Utils.isTrue(forceReload)) {
            this.onForceReload.emit();
        }
    }

    onWorkOrderTaskClose(): void {
        this.showTaskViewer = false;
    }

    createMaintenanceJob(): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.workOrderId = this.selectedWorkOrder.workOrderId;
        baseParams.communityId = this.selectedWorkOrder.communityId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.createMaintenanceJob(baseParams).subscribe(
            (result: WorkOrderCreateJobResultDto) => {
                this.workOrderMessageBox.showInfoMessage(
                    'Success',
                    `Successfully created maintenance job #${result.newMaintenanceJobId} for work order #${this.selectedWorkOrder.workOrderId}`
                );
                this.forceReloadWorkOrder();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    onManageProposalShow(): void {
        this.showManageProposals = true;
    }

    onManageProposalClose(forceReload: boolean): void {
        if (Utils.isTrue(forceReload)) {
            this.forceReloadWorkOrder();
        }

        this.showManageProposals = false;
    }

    onManageWorkAuthorizationShow(): void {
        this.showManageWorkAuthorization = true;
    }

    onManageWorkorderAuthorizationClose(): void {
        this.showManageWorkAuthorization = false;
    }

    onWorkOrderEditorShow(): void {
        this.showWorkOrderEditor = true;
    }

    onWorkOrderEditorClose(forceReload: boolean): void {
        this.showWorkOrderEditor = false;
        if (Utils.isTrue(forceReload)) {
            this.forceReloadWorkOrder();
        }
    }

    onRowSelection(event: any): void {
        const selectedKeys = this.workOrderGrid.instance.getSelectedRowKeys();
        this.onWorkOrderSelection.emit(selectedKeys);

        const deselectedKeys = event.currentDeselectedRowKeys;
        this.onWorkOrderDeselection.emit(deselectedKeys);
    }

    private openWorkOrderFile(): void {
        // NOTE: Since API is returning workAuthorizationFileName = null.
        const fileName = Utils.notNullAndDefined(this.selectedWorkOrder.workAuthorizationFileName)
            ? this.selectedWorkOrder.workAuthorizationFileName
            : `${this.selectedWorkOrder.workAuthorizationFileStoreId}_${this.selectedWorkOrder.workOrderId}.pdf`;

        const baseParams: WorkOrderWorkAuthorizationFileParameterDto = new WorkOrderWorkAuthorizationFileParameterDto();
        baseParams.fileId = this.selectedWorkOrder.workAuthorizationFileStoreId;
        baseParams.communityId = this.selectedWorkOrder.communityId;
        baseParams.workOrderId = this.selectedWorkOrder.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.downloadWorkAuthorizationDocument(baseParams).subscribe(
            (fileData: Blob) => {
                this.workOrderHelperService.hideLoading();
                this.helperService.showBlobInBrowserTab(fileData, fileName);
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private changeStatus(status: string): void {
        const baseParams: WorkOrderStatusChangeParametersDto = new WorkOrderStatusChangeParametersDto();
        baseParams.communityId = this.selectedWorkOrder.communityId;
        baseParams.workOrderId = this.selectedWorkOrder.workOrderId;
        baseParams.status = status;

        this.workOrderHelperService.showLoading();
        this.workOrderService.changeStatus(baseParams).subscribe(
            (result: any) => {
                notify('Status Changed successfully.', 'Success', MessageInterval.successMsgInterval);
                this.forceReloadWorkOrder();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateAttachments(clearCache = false): void {
        this.workOrderHelperService.showLoading();

        const apisToCall: Array<any> = [];

        const baseWorkOrderParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseWorkOrderParams.communityId = this.selectedWorkOrder.communityId;
        baseWorkOrderParams.workOrderId = this.selectedWorkOrder.workOrderId;

        apisToCall.push(this.workOrderService.getWorkOrderAttachments(baseWorkOrderParams));

        if (Utils.isTrue(this.isInternalUser)) {
            const baseCommunityParams: BasePropertyParametersDto = new BasePropertyParametersDto();
            baseCommunityParams.communityId = this.selectedWorkOrder.communityId;
            baseCommunityParams.clearCache = clearCache;
            apisToCall.push(this.workOrderService.getWorkOrderCommunityDocumentsTree(baseCommunityParams));

            if (Utils.notNullOrEmpty(this.selectedWorkOrder.customerId)) {
                const baseOwnerParams: WorkOrderOwnerDocumentsParameterDto = new WorkOrderOwnerDocumentsParameterDto();
                baseOwnerParams.communityId = this.selectedWorkOrder.communityId;
                baseOwnerParams.customerId = this.selectedWorkOrder.customerId;
                baseOwnerParams.clearCache = clearCache;
                apisToCall.push(this.workOrderService.getWorkOrderOwnerDocumentsTree(baseOwnerParams));
            }
        }
        forkJoin(apisToCall).subscribe(
            (apiResponse) => {
                this.workOrderAttachments = apiResponse[0];
                this.communityDocuments = apiResponse[1];

                if (Utils.notNullOrEmpty(this.selectedWorkOrder.customerId)) {
                    this.ownerDocuments = apiResponse[2];
                }

                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private deleteAttachment(attachment: FileInfoDisplayResultDto): void {
        const baseParams: WorkOrderAttachmentParametersDto = new WorkOrderAttachmentParametersDto();
        baseParams.fileId = attachment.fileId;
        baseParams.communityId = this.selectedWorkOrder.communityId;
        baseParams.workOrderId = this.selectedWorkOrder.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.deleteWorkorderAttachments(baseParams).subscribe(
            (result) => {
                notify('Document deleted successfully.', 'Success', MessageInterval.successMsgInterval);
                this.workOrderHelperService.hideLoading();
                setTimeout(() => this.populateAttachments(true), 100);
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private uploadAttachments(attachments: WorkOrderAttachmentRawDto[]): void {
        const baseParams: WorkOrderManageAttachmentsParametersDto = new WorkOrderManageAttachmentsParametersDto();
        baseParams.communityId = this.selectedWorkOrder.communityId;
        baseParams.workOrderId = this.selectedWorkOrder.workOrderId;
        baseParams.documents = attachments;

        this.workOrderHelperService.showLoading();
        this.workOrderService.uploadWorkorderAttachments(baseParams).subscribe(
            (result: any) => {
                notify('Document uploaded successfully.', 'success', MessageInterval.successMsgInterval);
                this.workOrderHelperService.hideLoading();
                setTimeout(() => this.populateAttachments(true), 100);
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private generateWorkAuthorization(communityId: number, workOrderId: number, emailAuthorization = false): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.communityId = communityId;
        baseParams.workOrderId = workOrderId;

        this.workOrderHelperService.showLoading();

        this.workOrderService.generateWorkAuthorization(baseParams).subscribe(
            (result: any) => {
                if (Utils.isTrue(emailAuthorization)) {
                    this.emailWorkAuthorization(communityId, workOrderId);
                } else {
                    notify('Work Authorization generated successfully.', 'Success', MessageInterval.successMsgInterval);
                }
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private forceReloadWorkOrder(): void {
        this.onForceReload.emit();
    }

    private prepareUniqueIds(): void {
        const invoiceIds: Array<any> = [];
        const taskIds: Array<any> = [];

        if (Utils.notNullOrEmpty(this.workOrdersGridSource)) {
            this.workOrdersGridSource.forEach((row) => {
                if (Utils.notNullOrEmpty(row.invoiceIds)) {
                    row.invoiceIds.forEach((id) => {
                        if (!invoiceIds.find((d) => Utils.equals(id, d.text))) {
                            invoiceIds.push({
                                text: id,
                                value: row.invoiceIds.join(', ')
                            });
                        }
                    });
                }

                if (Utils.notNullOrEmpty(row.taskIds)) {
                    row.taskIds.forEach((id) => {
                        if (!taskIds.find((d) => Utils.equals(id, d.text))) {
                            taskIds.push({
                                text: id,
                                value: row.taskIds.join(', ')
                            });
                        }
                    });
                }
            });
        }

        this.uniqueInvoiceIds = invoiceIds;
        this.uniqueTaskIds = taskIds;
    }

    private prepareUniqueUsers(): void {
        this.uniqueDashboardResponsibleUsers = this.helperService.prepareUniqueResponsibleUsers(
            this.workOrdersGridSource,
            'dashboardResponsible'
        );

        this.uniquePrimaryAcamUsers = this.helperService.prepareUniqueResponsibleUsers(
            this.workOrdersGridSource,
            'primaryAcam'
        );
        this.uniquePrimaryCamUsers = this.helperService.prepareUniqueResponsibleUsers(
            this.workOrdersGridSource,
            'primaryCam'
        );
    }

    private prepareActionItems(): void {
        this.actionMenuItems = [
            {
                icon: null,
                action: WorkOrderActions.VIEW_OR_EDIT_WORKORDER,
                name: 'View/Edit',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.GENERATE_WORK_AUTHORIZATION,
                name: 'Generate Work Authorization',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.GENERATE_AND_EMAIL_WORK_AUTHORIZATION,
                name: 'Generate and Email Work Authorization',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.EMAIL_LATEST_WORK_AUTHORIZATION,
                name: 'Email Latest Work Authorization',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.CHANGE_STATUS,
                name: 'Change Status',
                parent: null,
                disabled: false,
                items: [
                    {
                        icon: null,
                        action: StatusConstants.REQUEST,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'Initial Request',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.APPROVED,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'Approved',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.OUTTOBID,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'Out To Bid',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.COMPLETED,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'Completed',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.CANCELLED,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'Canceled',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.INPROGRESS,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'In Progress',
                        disabled: false,
                        items: []
                    },
                    {
                        icon: null,
                        action: StatusConstants.ONHOLD,
                        parent: WorkOrderActions.CHANGE_STATUS,
                        name: 'On Hold',
                        disabled: false,
                        items: []
                    }
                ]
            },
            {
                icon: null,
                action: WorkOrderActions.MANAGE_WORK_AUTHORIZATION,
                parent: null,
                name: 'Manage Work Authorization',
                disabled: false,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.MANAGE_PROPOSALS,
                parent: null,
                name: 'Manage Proposals',
                disabled: false,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.MANAGE_TASK_REFERENCES,
                parent: null,
                name: 'Manage Task References',
                disabled: false,
                items: []
            },
            {
                icon: null,
                action: WorkOrderActions.MANAGE_INVOICE_REFERENCES,
                parent: null,
                name: 'Manage Invoice References',
                disabled: false,
                items: []
            },
            {
                action: WorkOrderActions.CREATE_MAINTENANCE_JOB,
                parent: null,
                name: 'Create Maintenance Job',
                disabled: false,
                items: [],
                securityIndicator: 'SHIELD',
                apiAction: 'POST',
                apiUrl: 'api/workorder/maintenancejob'
            }
        ];
    }
}
